/**
 * WebOs Kernel
 *
 * Point d'entrée principal du système.
 * Gère le boot, les processus, l'IPC et l'orchestration des composants.
 */

import { createLogger } from '../utils/debug.js';
import { getBasePath, getVfsUrl } from '../utils/paths.js';
import { getFileSystem } from './filesystem.js';
import { initializeFileSystem } from './initial-fs.js';
import { getProcessManager } from './process-manager.js';
import { getIPCHandler } from './ipc-handler.js';
import { getGatekeeper } from './gatekeeper.js';
import { getAuth } from './auth.js';
import { getNotificationService } from './notification-service.js';
import { Shell } from '../shell/shell.js';
import { Login } from '../shell/login.js';

const log = createLogger('Kernel');

const KERNEL_VERSION = '0.6.0';

// Références DOM
const bootScreen = document.getElementById('boot-screen');
const bootMessage = document.getElementById('boot-message');
const bootProgress = document.getElementById('boot-progress');
const desktop = document.getElementById('desktop');

// État global du Kernel
const KernelState = {
  version: KERNEL_VERSION,
  bootTime: null,
  swRegistration: null,
  fs: null,
  processManager: null,
  ipcHandler: null,
  gatekeeper: null,
  notificationService: null,
  auth: null,
  currentUser: null,
  shell: null,
  ready: false
};

/**
 * Met à jour l'affichage du boot
 */
function updateBootStatus(message, progress, status = 'normal') {
  log.debug(`Boot: ${message} (${progress}%)`);

  bootMessage.textContent = message;
  bootProgress.style.width = `${progress}%`;

  const statusContainer = bootMessage.parentElement;
  statusContainer.classList.remove('boot-error', 'boot-success');

  if (status === 'error') {
    statusContainer.classList.add('boot-error');
  } else if (status === 'success') {
    statusContainer.classList.add('boot-success');
  }
}

// Chemin de base (importé depuis utils/paths.js)
const BASE_PATH = getBasePath();

/**
 * Enregistre le Service Worker VFS
 */
async function registerServiceWorker() {
  const timer = log.time('Service Worker registration');

  if (!('serviceWorker' in navigator)) {
    throw new Error('Service Workers non supportés dans ce navigateur');
  }

  log.debug('Base path detected:', BASE_PATH);
  log.debug('Registering Service Worker...');

  // Utiliser le chemin de base pour le SW et le scope
  const swPath = BASE_PATH + 'sw-vfs.js';
  const swScope = BASE_PATH;

  log.debug('SW path:', swPath);
  log.debug('SW scope:', swScope);

  const registration = await navigator.serviceWorker.register(swPath, {
    scope: swScope
  });

  log.debug('SW registered, state:', registration.active?.state || 'installing');

  if (registration.installing || registration.waiting) {
    log.debug('Waiting for SW activation...');

    await new Promise((resolve, reject) => {
      const sw = registration.installing || registration.waiting;

      const timeout = setTimeout(() => {
        reject(new Error('Service Worker activation timeout'));
      }, 10000);

      sw.addEventListener('statechange', () => {
        log.debug('SW state changed:', sw.state);
        if (sw.state === 'activated') {
          clearTimeout(timeout);
          resolve();
        }
        if (sw.state === 'redundant') {
          clearTimeout(timeout);
          reject(new Error('Service Worker became redundant'));
        }
      });
    });
  }

  // Attendre que le SW contrôle réellement la page (clients.claim())
  if (!navigator.serviceWorker.controller) {
    log.debug('Waiting for SW to claim control...');
    await new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Service Worker claim timeout'));
      }, 5000);

      navigator.serviceWorker.addEventListener('controllerchange', () => {
        log.debug('SW now controlling page');
        clearTimeout(timeout);
        resolve();
      }, { once: true });
    });
  } else {
    log.debug('SW already controlling page');
  }

  timer.end();
  log.info('Service Worker VFS active');

  return registration;
}

/**
 * Invalide le cache du Service Worker
 */
async function invalidateSWCache() {
  if (KernelState.swRegistration?.active) {
    KernelState.swRegistration.active.postMessage({
      type: 'INVALIDATE_CACHE'
    });
    log.debug('SW cache invalidation requested');
  }
}

/**
 * Initialise le système de fichiers
 */
async function initFileSystem() {
  const timer = log.time('FileSystem initialization');

  const fs = getFileSystem();
  await fs.init();

  KernelState.fs = fs;

  await initializeFileSystem();

  timer.end();
  log.info('FileSystem ready');
}

/**
 * Initialise le gestionnaire de processus et l'IPC
 */
async function initProcessSystem() {
  const timer = log.time('Process system initialization');

  // Initialiser les composants
  KernelState.processManager = getProcessManager();
  KernelState.ipcHandler = getIPCHandler();
  KernelState.gatekeeper = getGatekeeper();

  // Configurer l'IPC avec ses dépendances
  KernelState.ipcHandler.configure(
    KernelState.processManager,
    KernelState.fs
  );

  timer.end();
  log.info('Process system ready');
}

/**
 * Test du VFS
 */
async function testVFS() {
  const timer = log.time('VFS test');

  try {
    const testContent = `WebOs VFS Test - ${Date.now()}`;
    await KernelState.fs.writeFile('/tmp/vfs-test.txt', testContent);

    // Utiliser getVfsUrl pour que le SW puisse intercepter la requête
    const vfsUrl = getVfsUrl('/tmp/vfs-test.txt');
    log.debug('VFS test URL:', vfsUrl);

    const response = await fetch(vfsUrl);

    if (!response.ok) {
      throw new Error(`VFS fetch failed: ${response.status}`);
    }

    const fetchedContent = await response.text();

    if (fetchedContent !== testContent) {
      throw new Error('VFS content mismatch');
    }

    await KernelState.fs.delete('/tmp/vfs-test.txt');

    timer.end();
    log.info('VFS test passed');

  } catch (error) {
    timer.end();
    log.error('VFS test failed:', error.message);
    throw error;
  }
}

/**
 * Installe libwebos dans le VFS
 */
async function installLibwebos() {
  const timer = log.time('libwebos installation');

  const fs = KernelState.fs;
  const libPath = '/usr/lib/libwebos';

  // Créer le répertoire si nécessaire
  if (!await fs.exists(libPath)) {
    await fs.mkdir(libPath, { recursive: true });
  }

  // Charger et installer chaque module de libwebos
  const modules = [
    'kernel.js',
    'fs.js',
    'stdio.js',
    'process.js',
    'ansi.js',
    'paths.js',
    'index.js'
  ];

  for (const module of modules) {
    try {
      // Charger le fichier depuis public/lib/libwebos (copié pour le build)
      const response = await fetch(`${BASE_PATH}lib/libwebos/${module}`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const content = await response.text();
      await fs.writeFile(`${libPath}/${module}`, content);

      log.debug(`Installed: ${libPath}/${module}`);
    } catch (error) {
      log.error(`Failed to install ${module}:`, error.message);
      throw error;
    }
  }

  // Invalider le cache SW pour les nouveaux fichiers
  await invalidateSWCache();

  timer.end();
  log.info(`libwebos installed (${modules.length} modules)`);
}

/**
 * Installe les bootstraps (TUI, GUI, Daemon) dans le VFS
 */
async function installTuiBootstrap() {
  const timer = log.time('Bootstraps installation');

  const fs = KernelState.fs;
  const shellLibPath = '/usr/lib/shell';

  // Créer le répertoire si nécessaire
  if (!await fs.exists(shellLibPath)) {
    await fs.mkdir(shellLibPath, { recursive: true });
  }

  // Fichiers des bootstraps à installer
  const files = [
    'worker-terminal.js',
    'tui-worker-bootstrap.js',
    'gui-bootstrap.js',
    'daemon-worker-bootstrap.js'
  ];

  for (const file of files) {
    try {
      const response = await fetch(`${BASE_PATH}lib/shell/${file}`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const content = await response.text();
      await fs.writeFile(`${shellLibPath}/${file}`, content);

      log.debug(`Installed: ${shellLibPath}/${file}`);
    } catch (error) {
      log.error(`Failed to install shell/${file}:`, error.message);
      throw error;
    }
  }

  // Invalider le cache SW
  await invalidateSWCache();

  timer.end();
  log.info(`Bootstraps installed (${files.length} files)`);
}

/**
 * Installe le WebOS SDK dans le VFS
 */
async function installWebosSDK() {
  const timer = log.time('webos-sdk installation');

  const fs = KernelState.fs;
  const sdkPath = '/usr/lib/webos-sdk';

  // Créer les répertoires si nécessaire
  if (!await fs.exists(sdkPath)) {
    await fs.mkdir(sdkPath, { recursive: true });
  }
  if (!await fs.exists(`${sdkPath}/ui`)) {
    await fs.mkdir(`${sdkPath}/ui`, { recursive: true });
  }
  if (!await fs.exists(`${sdkPath}/gui`)) {
    await fs.mkdir(`${sdkPath}/gui`, { recursive: true });
  }

  // Liste des modules du SDK à installer
  const modules = [
    // Core
    'index.js',
    'base-app.js',
    'app.js',
    'gui-app.js',
    'daemon.js',
    'state.js',
    'hooks.js',
    'keys.js',
    'theme.js',
    // UI (TUI components)
    'ui/index.js',
    'ui/renderer.js',
    'ui/box.js',
    'ui/text.js',
    'ui/input.js',
    'ui/list.js',
    'ui/button.js',
    'ui/table.js',
    'ui/progress.js',
    'ui/dialog.js',
    // GUI components
    'gui/index.js',
    'gui/styles.js',
    'gui/window.js',
    'gui/panel.js',
    'gui/button.js',
    'gui/input.js',
    'gui/text.js'
  ];

  for (const module of modules) {
    try {
      const response = await fetch(`${BASE_PATH}lib/webos-sdk/${module}`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const content = await response.text();
      await fs.writeFile(`${sdkPath}/${module}`, content);

      log.debug(`Installed: ${sdkPath}/${module}`);
    } catch (error) {
      log.error(`Failed to install SDK module ${module}:`, error.message);
      throw error;
    }
  }

  // Invalider le cache SW pour les nouveaux fichiers
  await invalidateSWCache();

  timer.end();
  log.info(`webos-sdk installed (${modules.length} modules)`);
}

/**
 * Installe les applications de base dans le VFS
 */
async function installApps() {
  const timer = log.time('Apps installation');

  const fs = KernelState.fs;

  // Liste des apps à installer
  // Chaque app a un nom et une liste de fichiers à copier
  const apps = [
    {
      name: 'nano',
      path: '/usr/bin/nano',
      files: ['manifest.json', 'nano.js']
    }
  ];

  // Registre help pour les apps externes
  const helpRegistry = {};

  for (const app of apps) {
    try {
      // Créer le répertoire de l'app
      if (!await fs.exists(app.path)) {
        await fs.mkdir(app.path, { recursive: true });
      }

      // Copier chaque fichier
      let manifestContent = null;
      for (const file of app.files) {
        const response = await fetch(`${BASE_PATH}apps/${app.name}/${file}`);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status} for ${file}`);
        }

        const content = await response.text();
        await fs.writeFile(`${app.path}/${file}`, content);

        // Garder le manifest pour l'enregistrement help
        if (file === 'manifest.json') {
          manifestContent = content;
        }

        log.debug(`Installed: ${app.path}/${file}`);
      }

      // Enregistrer dans help si le manifest contient les infos
      if (manifestContent) {
        try {
          const manifest = JSON.parse(manifestContent);
          if (manifest.help) {
            const category = manifest.help.category || 'Applications';
            const usage = manifest.help.usage || manifest.name;
            const description = manifest.help.shortDescription || manifest.description;

            if (!helpRegistry[category]) {
              helpRegistry[category] = [];
            }
            helpRegistry[category].push({
              name: usage,
              description: description
            });

            log.debug(`Registered in help: ${manifest.name} -> ${category}`);
          }
        } catch (e) {
          log.warn(`Failed to parse manifest for ${app.name}`);
        }
      }

      log.info(`App installed: ${app.name}`);
    } catch (error) {
      log.error(`Failed to install ${app.name}:`, error.message);
      // Continuer avec les autres apps
    }
  }

  // Sauvegarder le registre help
  if (Object.keys(helpRegistry).length > 0) {
    try {
      await fs.writeFile('/usr/share/help-registry.json', JSON.stringify(helpRegistry, null, 2));
      log.debug('Help registry saved');
    } catch (e) {
      log.warn('Failed to save help registry:', e.message);
    }
  }

  // Invalider le cache SW
  await invalidateSWCache();

  timer.end();
  log.info(`Apps installed (${apps.length} apps)`);
}

/**
 * Prépare le container du desktop
 */
function prepareDesktop() {
  desktop.innerHTML = '';
  desktop.style.height = '100vh';
  desktop.style.width = '100vw';
  desktop.style.padding = '0';
  desktop.style.margin = '0';
  desktop.style.overflow = 'hidden';

  const rect = desktop.getBoundingClientRect();
  log.debug(`Container dimensions: ${rect.width}x${rect.height}`);

  if (rect.width === 0 || rect.height === 0) {
    log.warn('Container has zero dimensions!');
  }
}

/**
 * Lance l'écran de login
 */
async function runLogin() {
  const timer = log.time('Login');

  prepareDesktop();

  // Créer et lancer le login
  const login = new Login(desktop, KernelState.fs);
  await login.init();

  const result = await login.run();

  // Nettoyer l'écran de login
  login.destroy();
  desktop.innerHTML = '';

  timer.end();

  if (result.success) {
    KernelState.currentUser = result.user;
    KernelState.auth = getAuth();
    log.info('User logged in:', result.user.username);
    return true;
  } else {
    log.error('Login failed');
    return false;
  }
}

/**
 * Démarre le shell
 */
async function startShell() {
  const timer = log.time('Shell startup');

  prepareDesktop();

  // Créer et initialiser le shell avec l'utilisateur courant
  KernelState.shell = new Shell(desktop, KernelState.fs, KernelState.currentUser);
  await KernelState.shell.init();

  // Configurer le callback pour l'exécution de commandes via IPC
  KernelState.ipcHandler.onExecCommand = async (command, args, options) => {
    return KernelState.shell.execCommand(command, args, options);
  };

  timer.end();
  log.info('Shell started for user:', KernelState.currentUser?.username);
}

/**
 * Démarre les daemons configurés en autostart
 */
async function startAutostartDaemons() {
  const timer = log.time('Autostart daemons');

  try {
    // Lire la liste des paquets installés
    const installedPath = '/var/lib/webpkg/installed.json';
    if (!await KernelState.fs.exists(installedPath)) {
      log.debug('No installed packages found');
      timer.end();
      return;
    }

    const content = await KernelState.fs.readFile(installedPath);
    const installed = JSON.parse(content);

    // Trouver les daemons avec autostart
    const autostartDaemons = Object.entries(installed)
      .filter(([name, pkg]) => pkg.type === 'daemon' && pkg.autostart);

    if (autostartDaemons.length === 0) {
      log.debug('No autostart daemons configured');
      timer.end();
      return;
    }

    log.info(`Starting ${autostartDaemons.length} autostart daemon(s)...`);

    for (const [name, pkg] of autostartDaemons) {
      try {
        const appPath = `/usr/bin/${name}`;
        const manifestContent = await KernelState.fs.readFile(`${appPath}/manifest.json`);
        const manifest = JSON.parse(manifestContent);

        const moduleUrl = getVfsUrl(`${appPath}/${manifest.entry}`);
        const module = await import(moduleUrl);
        const AppClass = module.default || module[Object.keys(module)[0]];

        if (AppClass) {
          // Créer et démarrer le daemon avec "start" comme commande
          const daemon = new AppClass(null, KernelState.fs, 'start');
          await daemon.start();
          log.info(`Autostart daemon started: ${name}`);
        }
      } catch (err) {
        log.error(`Failed to start daemon ${name}:`, err.message);
      }
    }

    timer.end();
  } catch (err) {
    log.error('Error starting autostart daemons:', err.message);
    timer.end();
  }
}

/**
 * Affiche les informations système
 */
function logSystemInfo() {
  const group = log.group('System Information', false);

  log.info(`WebOs v${KERNEL_VERSION}`);
  log.info(`User Agent: ${navigator.userAgent}`);
  log.info(`Platform: ${navigator.platform}`);
  log.info(`Language: ${navigator.language}`);
  log.info(`Online: ${navigator.onLine}`);
  log.info(`Cores: ${navigator.hardwareConcurrency || 'unknown'}`);

  if (navigator.storage?.estimate) {
    navigator.storage.estimate().then(({ usage, quota }) => {
      const usedMB = (usage / 1024 / 1024).toFixed(2);
      const quotaMB = (quota / 1024 / 1024).toFixed(2);
      log.info(`Storage: ${usedMB}MB / ${quotaMB}MB`);
    });
  }

  group.end();
}

/**
 * Séquence de boot principale
 */
async function boot() {
  KernelState.bootTime = Date.now();

  console.clear();
  log.info(`WebOs v${KERNEL_VERSION} - Boot sequence started`);
  logSystemInfo();

  try {
    // Étape 1 : Service Worker
    updateBootStatus('Chargement du système de fichiers virtuel...', 10);
    KernelState.swRegistration = await registerServiceWorker();

    // Étape 2 : FileSystem
    updateBootStatus('Initialisation du stockage...', 25);
    await initFileSystem();

    // Étape 3 : Test VFS
    updateBootStatus('Vérification du VFS...', 40);
    await testVFS();

    // Étape 4 : Système de processus
    updateBootStatus('Initialisation du gestionnaire de processus...', 50);
    await initProcessSystem();

    // Étape 5 : Gatekeeper
    updateBootStatus('Configuration de la sécurité...', 60);
    await new Promise(r => setTimeout(r, 100));
    log.info('Gatekeeper ready');

    // Étape 6 : Installation de libwebos
    updateBootStatus('Installation de libwebos...', 65);
    await installLibwebos();

    // Étape 6.5 : Installation du SDK
    updateBootStatus('Installation du SDK...', 70);
    await installWebosSDK();

    // Étape 6.6 : Installation du bootstrap TUI
    updateBootStatus('Installation du bootstrap TUI...', 75);
    await installTuiBootstrap();

    // Étape 7 : Installation des applications
    updateBootStatus('Installation des applications...', 80);
    await installApps();

    // Boot terminé
    KernelState.ready = true;
    const bootDuration = Date.now() - KernelState.bootTime;

    updateBootStatus('Système prêt', 100, 'success');
    log.info(`Boot completed in ${bootDuration}ms`);

    // Transition vers le desktop
    await new Promise(r => setTimeout(r, 300));
    bootScreen.classList.add('fade-out');

    // Rendre le desktop visible
    desktop.classList.remove('hidden');

    // Attendre que le CSS soit appliqué
    await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));

    // Cacher le boot screen
    setTimeout(() => {
      bootScreen.style.display = 'none';
    }, 200);

    // Étape 8 : Démarrer le service de notifications
    KernelState.notificationService = getNotificationService();
    KernelState.notificationService.start();
    log.info('Notification service started');

    // Étape 9 : Login (premier boot = assistant, sinon = login)
    const loggedIn = await runLogin();

    if (!loggedIn) {
      log.error('Login failed, halting boot');
      return;
    }

    // Étape 10 : Démarrage du shell
    await startShell();

    // Étape 11 : Démarrer les daemons autostart
    await startAutostartDaemons();

  } catch (error) {
    log.error('Boot failed:', error);
    updateBootStatus(`Erreur: ${error.message}`, 0, 'error');
  }
}

// Exposer l'API du Kernel pour debug
window.__WEBOS_KERNEL__ = {
  state: KernelState,
  version: KERNEL_VERSION,

  // FileSystem
  fs: () => getFileSystem(),

  // ProcessManager
  spawn: (bin, args, options) => getProcessManager().spawn(bin, args, options),
  kill: (pid, force) => getProcessManager().terminateProcess(pid, 0, force),
  listProcesses: () => getProcessManager().listProcesses(),
  getProcess: (pid) => getProcessManager().getProcess(pid),

  // Gatekeeper
  validateManifest: (m) => getGatekeeper().validateManifest(m),
  checkPermission: (perms, path, op) => getGatekeeper().checkFileAccess(perms, path, op),

  // Notifications
  notify: (title, message, type, duration) => getNotificationService().send(title, message, type, duration),
  notificationService: () => getNotificationService(),

  // Shell
  shell: () => KernelState.shell,

  // Créer une nouvelle instance de shell pour un container
  // Options: { existingTerminal, fitAddon, skipWelcome, skipAutostart }
  createShell: async (container, options = {}) => {
    const shell = new Shell(container, getFileSystem(), KernelState.currentUser, options);
    await shell.init();
    return shell;
  },

  // Utilitaires
  invalidateCache: invalidateSWCache,
  reboot: () => location.reload()
};

// Exposer aussi le FS directement
window.__WEBOS_FS__ = {
  getInstance: () => getFileSystem(),
  readFile: (path) => getFileSystem().readFile(path),
  writeFile: (path, data) => getFileSystem().writeFile(path, data),
  readdir: (path) => getFileSystem().readdir(path),
  stat: (path) => getFileSystem().stat(path),
  mkdir: (path, opts) => getFileSystem().mkdir(path, opts),
  delete: (path, opts) => getFileSystem().delete(path, opts),
  exists: (path) => getFileSystem().exists(path),
  symlink: (target, path) => getFileSystem().symlink(target, path),
  listAll: () => getFileSystem().listAll()
};

// Lancer le boot
boot();
